import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import MediaUploader from '../../components/MediaUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SiteSettingsManager = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/settings`);
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "Failed to load site settings",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleUpdate = async (key, value) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/settings/${key}`, { value });
      toast({
        title: "Success",
        description: `${key} updated successfully`
      });
      fetchSettings();
    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: "Error",
        description: "Failed to update setting",
        variant: "destructive"
      });
    }
  };

  const settingsConfig = [
    // Branding
    { key: 'site_logo', label: 'Site Logo', type: 'media', category: 'Branding' },
    { key: 'site_name', label: 'Site Name', type: 'text', category: 'Branding' },
    
    // Contact Information
    { key: 'contact_email', label: 'Contact Email', type: 'email', category: 'Contact' },
    { key: 'contact_phone', label: 'Contact Phone', type: 'text', category: 'Contact' },
    { key: 'whatsapp_number', label: 'WhatsApp Number (with country code)', type: 'text', category: 'Contact' },
    { key: 'contact_address', label: 'Contact Address', type: 'text', category: 'Contact' },
    
    // Hero Section
    { key: 'hero_title', label: 'Hero Title', type: 'text', category: 'Hero' },
    { key: 'hero_highlight', label: 'Hero Highlight', type: 'text', category: 'Hero' },
    
    // Services Section
    { key: 'services_title', label: 'Services Section Title', type: 'text', category: 'Services' },
    { key: 'services_subtitle', label: 'Services Subtitle', type: 'text', category: 'Services' },
    
    // CTA Section
    { key: 'cta_title', label: 'CTA Title', type: 'text', category: 'CTA' },
    { key: 'cta_subtitle', label: 'CTA Subtitle', type: 'text', category: 'CTA' },
    { key: 'cta_brand', label: 'CTA Brand Name', type: 'text', category: 'CTA' },
    
    // Theme/Styling
    { key: 'font_family', label: 'Font Family', type: 'select', category: 'Theme', options: ['Montserrat', 'Inter', 'Roboto', 'Playfair Display', 'Poppins', 'Open Sans'] },
    { key: 'primary_color', label: 'Primary Color', type: 'color', category: 'Theme' },
    { key: 'secondary_color', label: 'Secondary Color', type: 'color', category: 'Theme' },
    { key: 'text_color', label: 'Text Color', type: 'color', category: 'Theme' },
    { key: 'background_color', label: 'Background Color', type: 'color', category: 'Theme' },
    
    // Legal
    { key: 'privacy_policy', label: 'Privacy Policy', type: 'textarea', category: 'Legal' },
    { key: 'terms_of_service', label: 'Terms of Service', type: 'textarea', category: 'Legal' },
  ];

  // Group settings by category
  const groupedSettings = settingsConfig.reduce((acc, config) => {
    if (!acc[config.category]) {
      acc[config.category] = [];
    }
    acc[config.category].push(config);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-12">Loading settings...</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-light text-[#2d2d2d] mb-8">Site Settings & Theme</h1>

      {Object.entries(groupedSettings).map(([category, configs]) => (
        <div key={category} className="mb-8">
          <h2 className="text-2xl font-light text-[#0A1A50] mb-4">{category} Settings</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {configs.map((config) => (
              <div key={config.key} className="bg-white p-6 rounded-lg shadow-lg">
                {config.type === 'media' ? (
                  <div>
                    <MediaUploader
                      label={config.label}
                      currentUrl={settings[config.key] || ''}
                      onMediaSelect={(url) => setSettings({ ...settings, [config.key]: url })}
                      accept="image/*"
                    />
                    <Button
                      onClick={() => handleUpdate(config.key, settings[config.key])}
                      className="bg-[#0A1A50] hover:bg-[#060F2E] text-white w-full mt-4"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save {config.label}
                    </Button>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
                      {config.label}
                    </label>
                    {config.type === 'textarea' ? (
                      <Textarea
                        value={settings[config.key] || ''}
                        onChange={(e) => setSettings({ ...settings, [config.key]: e.target.value })}
                        className="mb-2"
                        rows={config.category === 'Legal' ? 15 : 4}
                      />
                    ) : config.type === 'color' ? (
                      <div className="flex gap-2 mb-2">
                        <input
                          type="color"
                          value={settings[config.key] || '#000000'}
                          onChange={(e) => setSettings({ ...settings, [config.key]: e.target.value })}
                          className="w-20 h-10 rounded cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={settings[config.key] || ''}
                          onChange={(e) => setSettings({ ...settings, [config.key]: e.target.value })}
                          placeholder="#000000"
                          className="flex-1"
                        />
                      </div>
                    ) : config.type === 'select' ? (
                      <select
                        value={settings[config.key] || config.options[0]}
                        onChange={(e) => setSettings({ ...settings, [config.key]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                      >
                        {config.options.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={config.type}
                        value={settings[config.key] || ''}
                        onChange={(e) => setSettings({ ...settings, [config.key]: e.target.value })}
                        className="mb-2"
                      />
                    )}
                    <Button
                      onClick={() => handleUpdate(config.key, settings[config.key])}
                      className="bg-[#0A1A50] hover:bg-[#060F2E] text-white w-full"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save {config.label}
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-blue-50 p-6 rounded-lg mt-6">
        <h3 className="text-lg font-normal text-blue-900 mb-2">💡 Theme Tips</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Font changes apply site-wide</li>
          <li>• Primary color affects buttons, headings, and hero highlights</li>
          <li>• Secondary color affects subtitles and descriptions</li>
          <li>• Changes are reflected immediately on the live site</li>
        </ul>
      </div>
    </div>
  );
};

export default SiteSettingsManager;